import React, { useEffect, useMemo, useRef, useState } from 'react'
import Markdown from './Markdown'

export interface Bloco {
  tipo: 'texto' | 'tabela' | 'grafico'
  texto?: string | null
  titulo?: string | null
  colunas?: string[] | null
  linhas?: any[][] | null
  especificacao?: any
}

const LINHAS_VISIVEIS = 50

const numero = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 })

function valor(v: any): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'number') return numero.format(v)
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function baixarCsv(colunas: string[], linhas: any[][], titulo?: string | null) {
  const campo = (v: any) => {
    const t = v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)
    return /[";\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t
  }
  const texto = [colunas, ...linhas].map(l => l.map(campo).join(';')).join('\r\n')
  const url = URL.createObjectURL(new Blob(['﻿' + texto], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `${(titulo || 'oneflow').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'oneflow'}.csv`
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function Tabela({ bloco }: { bloco: Bloco }) {
  const [todas, setTodas] = useState(false)
  const colunas = bloco.colunas || []
  const linhas = bloco.linhas || []
  const visiveis = todas ? linhas : linhas.slice(0, LINHAS_VISIVEIS)

  return (
    <div className="of-bloco">
      <div className="of-bloco-topo">
        <span className="of-bloco-titulo">{bloco.titulo || 'Tabela'}</span>
        <button type="button" className="of-link" onClick={() => baixarCsv(colunas, linhas, bloco.titulo)}>Baixar CSV</button>
      </div>
      <div className="of-tabela-rolagem">
        <table className="of-tabela">
          <thead><tr>{colunas.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
          <tbody>
            {visiveis.map((l, r) => (
              <tr key={r}>{colunas.map((_, i) => <td key={i} className={typeof l[i] === 'number' ? 'of-num' : ''}>{valor(l[i])}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
      {linhas.length > LINHAS_VISIVEIS && (
        <button type="button" className="of-link" onClick={() => setTodas(t => !t)}>
          {todas ? 'Mostrar menos' : `Mostrar todas as ${numero.format(linhas.length)} linhas`}
        </button>
      )}
    </div>
  )
}

/**
 * O gráfico é uma especificação Vega-Lite desenhada num iframe isolado: só scripts, sem a
 * origem do Portal, então nada ali alcança a sessão de quem está logado.
 */
function Grafico({ bloco }: { bloco: Bloco }) {
  const ref = useRef<HTMLIFrameElement>(null)
  const [altura, setAltura] = useState(260)
  const [falhou, setFalhou] = useState(false)

  const documento = useMemo(() => {
    const spec = JSON.stringify(bloco.especificacao ?? {}).replace(/</g, '\\u003c')
    return `<!doctype html><html><head><meta charset="utf-8">
<script src="https://cdn.jsdelivr.net/npm/vega@5"></script>
<script src="https://cdn.jsdelivr.net/npm/vega-lite@5"></script>
<script src="https://cdn.jsdelivr.net/npm/vega-embed@6"></script>
<style>html,body{margin:0;padding:0;background:transparent;font-family:Arial,Helvetica,sans-serif}#g{width:100%}</style>
</head><body><div id="g"></div><script>
var avisar = function (tipo, extra) { parent.postMessage(Object.assign({ oneflow: tipo }, extra || {}), '*') };
var medir = function () { avisar('altura', { altura: document.documentElement.scrollHeight }) };
try {
  var spec = ${spec};
  if (spec.mark && spec.width === undefined) { spec.width = 'container'; spec.autosize = spec.autosize || { type: 'fit-x', contains: 'padding' } }
  vegaEmbed('#g', spec, {
    actions: false,
    renderer: 'svg',
    formatLocale: { decimal: ',', thousands: '.', grouping: [3], currency: ['R$ ', ''] },
    timeFormatLocale: {
      dateTime: '%A, %e de %B de %Y. %X', date: '%d/%m/%Y', time: '%H:%M:%S', periods: ['AM', 'PM'],
      days: ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'], shortDays: ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'],
      months: ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'],
      shortMonths: ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']
    }
  }).then(function () { medir(); new ResizeObserver(medir).observe(document.body) })
    .catch(function () { avisar('erro') });
} catch (e) { avisar('erro') }
</script></body></html>`
  }, [bloco.especificacao])

  useEffect(() => {
    const ouvir = (e: MessageEvent) => {
      if (e.source !== ref.current?.contentWindow || !e.data) return
      if (e.data.oneflow === 'altura' && typeof e.data.altura === 'number') setAltura(Math.min(Math.max(e.data.altura, 80), 900))
      if (e.data.oneflow === 'erro') setFalhou(true)
    }
    window.addEventListener('message', ouvir)
    return () => window.removeEventListener('message', ouvir)
  }, [])

  return (
    <div className="of-bloco">
      {bloco.titulo && <div className="of-bloco-topo"><span className="of-bloco-titulo">{bloco.titulo}</span></div>}
      {falhou
        ? <div className="of-aviso">Não consegui desenhar este gráfico aqui.</div>
        : <iframe ref={ref} title={bloco.titulo || 'Gráfico do oneflow'} srcDoc={documento} sandbox="allow-scripts"
                  style={{ width: '100%', height: altura, border: 'none', display: 'block' }} />}
    </div>
  )
}

export default function Blocos({ blocos }: { blocos: Bloco[] }) {
  return (
    <>
      {blocos.map((b, i) => {
        if (b.tipo === 'tabela') return <Tabela key={i} bloco={b} />
        if (b.tipo === 'grafico') return <Grafico key={i} bloco={b} />
        return <Markdown key={i} texto={b.texto || ''} />
      })}
    </>
  )
}
